import { isToday } from 'date-fns'
import { type lngShort } from '../i18n.utils'
import chalk from 'chalk'

function isWithinDays(date: Date, days: number) {
	const now = new Date()
	const diffTime = date.getTime() - now.getTime()
	const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
	return diffDays >= 0 && diffDays < days && (isToday(date) || date > now)
}

function isSameDate(from: Date, to: Date) {
	// Check if the dates have the same year, month, and day
	const sameDate =
		from.getFullYear() === to.getFullYear() &&
		from.getMonth() === to.getMonth() &&
		from.getDate() === to.getDate()

	// If the dates are the same, return true
	if (sameDate) {
		return true
	} else {
		// If the dates are not the same, check if it's before 5 AM of the next day
		const nextDay = new Date(from)
		nextDay.setDate(nextDay.getDate() + 1)
		nextDay.setHours(5, 0, 0, 0) // Set time to 05:00 AM

		// Return true if date2 is before 5 AM of the next day
		return to < nextDay
	}
}

export function getShortDate({
	date,
	lng,
	timeZone,
}: {
	date: Date
	lng: lngShort
	timeZone: string
}) {
	// Check if the parameters are defined and throw an error if not
	if (!date || !lng || !timeZone) {
		// eslint-disable-next-line no-console
		console.error(
			chalk.red('getShortDate: Missing parameters'),
			date,
			lng,
			timeZone,
		)
	}
	let dateString = ''
	if (isWithinDays(date, 7)) {
		const today = isToday(date)
		if (today) {
			// TODO: Replace with translation 'misc_today'?
			if (lng === 'sv') {
				dateString += 'idag '
			} else if (lng === 'no') {
				dateString += 'i dag '
			} else if (lng === 'da') {
				dateString += 'i dag '
			} else if (lng === 'fi') {
				dateString += 'tänään '
			} else {
				dateString += 'today '
			}
		} else {
			dateString +=
				new Intl.DateTimeFormat(lng, {
					weekday: 'short',
					timeZone: timeZone,
				}).format(date) + ' '
		}
		dateString += date.toLocaleTimeString(lng, {
			hour: '2-digit',
			minute: '2-digit',
			timeZone: timeZone,
		})
		return dateString
	}
	return new Intl.DateTimeFormat(lng, {
		year: !isWithinDays(date, 365) ? '2-digit' : undefined,
		month: 'short',
		day: 'numeric',
		timeZone: timeZone,
	}).format(date)
}

function getMonthDateTime({
	date,
	lng,
	timeZone,
	hideDay = false,
	short = false,
}: {
	date: Date
	lng: lngShort
	timeZone: string
	hideDay?: boolean
	short?: boolean
}) {
	// Check if the parameters are defined and throw an error if not
	if (!date || !lng || !timeZone) {
		// eslint-disable-next-line no-console
		console.error(
			chalk.red('getMonthDateTime: Missing parameters'),
			date,
			lng,
			timeZone,
		)
	}
	const todayStr = () => {
		if (lng === 'en') {
			return 'today '
		}
		if (lng === 'no') {
			return 'idag '
		}
		if (lng === 'da') {
			return 'idag '
		}
		if (lng === 'fi') {
			return 'tänään '
		}
		return 'idag '
	}
	let dateString = ''
	if (isWithinDays(date, 7) && !hideDay) {
		const today = isToday(date)
		if (today) {
			dateString += todayStr()
		} else {
			dateString +=
				new Intl.DateTimeFormat(lng, {
					weekday: short ? 'short' : 'long',
					timeZone: timeZone,
				}).format(date) + ' '
		}
	} else if (!hideDay) {
		// For Finnish, use a consistent format for month on both server and client
		if (lng === 'fi' && short) {
			// Format like: "DD.MM. HH.MM" for Finnish short format to match server-side rendering
			const day = date.getDate()
			const month = date.getMonth() + 1
			const year = !isWithinDays(date, 365) ? date.getFullYear() : null

			if (year) {
				dateString += `${day}.${month}.${year} `
			} else {
				dateString += `${day}.${month}. `
			}
		} else {
			dateString +=
				new Intl.DateTimeFormat(lng, {
					year: !isWithinDays(date, 365)
						? short
							? '2-digit'
							: 'numeric'
						: undefined,
					month: short ? 'short' : 'long',
					day: 'numeric',
					timeZone: timeZone,
				}).format(date) + ' '
		}
	}

	dateString += date.toLocaleTimeString(lng, {
		hour: '2-digit',
		minute: '2-digit',
		timeZone: timeZone,
	})

	return dateString
}

export function getMonthDateTimeFromTo({
	date,
	lng,
	timeZone,
	dateTo,
	short = false,
}: {
	date: Date
	lng: lngShort
	timeZone: string
	dateTo?: Date
	short?: boolean
}) {
	// Check if the parameters are defined and throw an error if not
	if (!date || !lng || !timeZone) {
		// eslint-disable-next-line no-console
		console.error(
			chalk.red('getMonthDateTimeFromTo: Missing parameters'),
			date,
			lng,
			timeZone,
		)
	}
	let dateString = ''
	dateString += getMonthDateTime({
		date,
		lng,
		timeZone: timeZone,
		hideDay: false,
		short: short,
	})

	dateTo &&
		((dateString +=
			' - ' +
			getMonthDateTime({
				date: dateTo,
				lng,
				timeZone: timeZone,
				hideDay: isSameDate(date, dateTo),
				short: short,
			})),
		lng)

	return dateString
}

export function toLocaleDateTime(date: Date, lng: lngShort) {
	return date.toLocaleString(lng, {
		dateStyle: 'short',
		timeStyle: 'short',
	})
}

export function toLocaleDate(date: Date, lng: lngShort) {
	return date.toLocaleString(lng, {
		dateStyle: 'short',
	})
}

export function toLocaleTime(date: Date, lng: lngShort) {
	return date.toLocaleString(lng, {
		timeStyle: 'short',
	})
}
